import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";

interface Props {
    content: string;
}

const components: Components = {
    table: ({ children, ...props }) => (
        <div style={{ overflowX: "auto", marginBottom: "16px", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
            <table
                {...props}
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.78rem",
                    lineHeight: 1.5,
                }}
            >
                {children}
            </table>
        </div>
    ),
    thead: ({ children, ...props }) => (
        <thead {...props} style={{ background: "var(--bg-secondary)" }}>
            {children}
        </thead>
    ),
    th: ({ children, ...props }) => (
        <th
            {...props}
            style={{
                padding: "8px 12px",
                textAlign: "left",
                fontWeight: 700,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.03em",
                color: "var(--text-muted)",
                borderBottom: "2px solid var(--border-color)",
            }}
        >
            {children}
        </th>
    ),
    td: ({ children, ...props }) => (
        <td
            {...props}
            style={{
                padding: "8px 12px",
                borderTop: "1px solid var(--border-color-light, var(--border-color))",
                color: "var(--text-primary)",
            }}
        >
            {children}
        </td>
    ),
    h2: ({ children, ...props }) => (
        <h2
            {...props}
            style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                marginTop: "20px",
                marginBottom: "10px",
                paddingBottom: "6px",
                borderBottom: "1px solid var(--border-color)",
            }}
        >
            {children}
        </h2>
    ),
    h3: ({ children, ...props }) => (
        <h3
            {...props}
            style={{
                fontSize: "0.88rem",
                fontWeight: 700,
                color: "var(--text-primary)",
                marginTop: "16px",
                marginBottom: "8px",
            }}
        >
            {children}
        </h3>
    ),
    p: ({ children, ...props }) => (
        <p
            {...props}
            style={{
                fontSize: "0.82rem",
                lineHeight: 1.65,
                color: "var(--text-secondary)",
                marginBottom: "10px",
            }}
        >
            {children}
        </p>
    ),
    strong: ({ children, ...props }) => (
        <strong {...props} style={{ color: "var(--text-primary)", fontWeight: 700 }}>
            {children}
        </strong>
    ),
    ul: ({ children, ...props }) => (
        <ul
            {...props}
            style={{
                paddingLeft: "20px",
                marginBottom: "12px",
                fontSize: "0.82rem",
                lineHeight: 1.65,
                color: "var(--text-secondary)",
            }}
        >
            {children}
        </ul>
    ),
    ol: ({ children, ...props }) => (
        <ol
            {...props}
            style={{
                paddingLeft: "20px",
                marginBottom: "12px",
                fontSize: "0.82rem",
                lineHeight: 1.65,
                color: "var(--text-secondary)",
            }}
        >
            {children}
        </ol>
    ),
    li: ({ children, ...props }) => (
        <li {...props} style={{ marginBottom: "4px" }}>
            {children}
        </li>
    ),
    blockquote: ({ children, ...props }) => (
        <blockquote
            {...props}
            style={{
                borderLeft: "3px solid #8b5cf6",
                paddingLeft: "12px",
                margin: "12px 0",
                color: "var(--text-secondary)",
                fontStyle: "italic",
                fontSize: "0.82rem",
                background: "rgba(139,92,246,0.04)",
                padding: "10px 12px",
                borderRadius: "0 6px 6px 0",
            }}
        >
            {children}
        </blockquote>
    ),
    hr: (props) => (
        <hr
            {...props}
            style={{
                border: "none",
                borderTop: "1px solid var(--border-color)",
                margin: "16px 0",
            }}
        />
    ),
    code: ({ children, ...props }) => (
        <code
            {...props}
            style={{
                background: "var(--bg-secondary)",
                padding: "1px 5px",
                borderRadius: "4px",
                fontSize: "0.78rem",
                fontFamily: "monospace",
                color: "#8b5cf6",
            }}
        >
            {children}
        </code>
    ),
};

export function MarkdownAnalysis({ content }: Props) {
    return (
        <div className="markdown-analysis">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {content}
            </ReactMarkdown>
        </div>
    );
}
